'use client';

import React, { createContext, JSX, PropsWithChildren, useContext, useMemo } from 'react';
import { useSetState } from 'react-use';

import { AiContentVoiceoverType } from '@videoblocks/events-ts/lib/storyblocks/content/ai/AiContentVoiceoverTypeEnum';

import type { Voice } from '../types';

/**
 * Here we track state storage for each individual tab. When switching tabs (/ urls), the UI
 * components are unmounted and re-mounted, causing internal state to be lost. This context
 * preserves any state needed for re-rendering (i.e. text prompts, generated audio URLs).
 */
export enum Tabs {
    TextToSpeech = 'textToSpeech',
    CloneMyVoice = 'cloneMyVoice',
    VoiceChanger = 'voiceChanger',
}

/**
 * map tab enum values to their corresponding AI content voiceover event types
 */
export const TAB_TO_EVENT_TYPE: Record<Tabs, AiContentVoiceoverType> = {
    [Tabs.TextToSpeech]: AiContentVoiceoverType.TEXT_TO_SPEECH,
    [Tabs.CloneMyVoice]: AiContentVoiceoverType.VOICE_CLONING,
    [Tabs.VoiceChanger]: AiContentVoiceoverType.VOICE_CHANGING,
};

// state types per tab
interface TabState {
    generatedAudioUrl: string | null;
    generatedAudioHasPlayed: boolean;
    // per-tab event tracking ids
    aiContentId: string | null;
    lastAiContentId: string | null;
}

export interface TextToSpeechState extends TabState {
    generatedAudioVoiceId: Voice['voice_id'] | null;
    script: string | null;
    calculatedCost: number;
    selectedVoice: Voice['voice_id'];
    voiceoverParams: Record<string, string | number>;
}

export interface CloneMyVoiceState extends TabState {
    isUpload: boolean;
    generatedVoiceId: Voice['voice_id'] | null;
    script: string | null;
    calculatedCost: number;
    userAudioPreviewUrl: string | null;
    userAudioHasPlayed: boolean;
    voiceoverParams: Record<string, string | number>;
}

export interface VoiceChangerState extends TabState {
    uploadedAudioUrl: string | null;
    uploadedAudioHasPlayed: boolean;
    selectedVoice: Voice['voice_id'];
    userFile: File | null;
}

// map each Tab to its corresponding state
type TabStateMap = {
    [Tabs.TextToSpeech]: TextToSpeechState;
    [Tabs.CloneMyVoice]: CloneMyVoiceState;
    [Tabs.VoiceChanger]: VoiceChangerState;
};

// make updateState generic on the tab
export type VoiceoverTabContextType = {
    textToSpeech: TextToSpeechState;
    cloneMyVoice: CloneMyVoiceState;
    voiceChanger: VoiceChangerState;
    updateState: <T extends Tabs>(newState: Partial<TabStateMap[T]>) => void;
    activeTabState: TextToSpeechState | CloneMyVoiceState | VoiceChangerState;
    activeTab: Tabs;
    setActiveTab: (tab: Tabs) => void;
};

// initial states per tab
export const initialTextToSpeech: TextToSpeechState = {
    generatedAudioUrl: null,
    generatedAudioHasPlayed: false,
    generatedAudioVoiceId: null, // voice id of generated audio
    script: null,
    calculatedCost: 0,
    selectedVoice: 'FUfBrNit0NNZAwb58KWH', // first voice in list (Angela)
    voiceoverParams: { speed: 100 },
    aiContentId: null,
    lastAiContentId: null,
};

export const initialCloneMyVoice: CloneMyVoiceState = {
    isUpload: true,
    generatedVoiceId: null,
    script: null,
    calculatedCost: 0,
    userAudioPreviewUrl: null,
    generatedAudioUrl: null,
    userAudioHasPlayed: false,
    generatedAudioHasPlayed: false,
    voiceoverParams: { speed: 100 },
    aiContentId: null,
    lastAiContentId: null,
};

export const initialVoiceChanger: VoiceChangerState = {
    uploadedAudioUrl: null,
    generatedAudioUrl: null,
    uploadedAudioHasPlayed: false,
    generatedAudioHasPlayed: false,
    selectedVoice: 'FUfBrNit0NNZAwb58KWH',
    userFile: null,
    aiContentId: null,
    lastAiContentId: null,
};

export const VoiceoverTabContext = createContext<VoiceoverTabContextType>({
    textToSpeech: initialTextToSpeech,
    cloneMyVoice: initialCloneMyVoice,
    voiceChanger: initialVoiceChanger,
    activeTabState: {} as TextToSpeechState | CloneMyVoiceState | VoiceChangerState,
    activeTab: Tabs.TextToSpeech,
    updateState: () => {},
    setActiveTab: () => {},
});

export function VoiceoverTabProvider({ children }: PropsWithChildren): JSX.Element {
    const [activeTab, setActiveTab] = React.useState<Tabs>(Tabs.TextToSpeech);
    const [textToSpeech, updateTextToSpeech] = useSetState<TextToSpeechState>(initialTextToSpeech);
    const [cloneMyVoice, updateCloneMyVoice] = useSetState<CloneMyVoiceState>(initialCloneMyVoice);
    const [voiceChanger, updateVoiceChanger] = useSetState<VoiceChangerState>(initialVoiceChanger);

    const updateState = (newState: Record<string, unknown>) => {
        switch (activeTab) {
            case Tabs.TextToSpeech:
                updateTextToSpeech({ ...newState });
                break;
            case Tabs.CloneMyVoice:
                updateCloneMyVoice({ ...newState });
                break;
            case Tabs.VoiceChanger:
                updateVoiceChanger({ ...newState });
                break;
        }
    };

    const activeTabState = useMemo(() => {
        switch (activeTab) {
            case Tabs.TextToSpeech:
                return textToSpeech;
            case Tabs.CloneMyVoice:
                return cloneMyVoice;
            case Tabs.VoiceChanger:
                return voiceChanger;
            default:
                return textToSpeech;
        }
    }, [activeTab, textToSpeech, cloneMyVoice, voiceChanger]);

    return (
        <VoiceoverTabContext.Provider
            value={{
                activeTab: activeTab,
                setActiveTab,
                activeTabState,
                updateState,
                textToSpeech,
                cloneMyVoice,
                voiceChanger,
            }}
        >
            {children}
        </VoiceoverTabContext.Provider>
    );
}

export function useVoiceoverTabContext(): VoiceoverTabContextType {
    const ctx = useContext(VoiceoverTabContext);
    if (!ctx) throw new Error('useVoiceoverTabContext must be used within VoiceoverTabProvider');
    return ctx;
}
